import spacetime, { Spacetime } from 'spacetime';
import { formatDuration } from './utils';

import { selection } from '../store/selection.store';
import { settings } from '../store/settings.store';
import { customer } from '../store/customer.store';
import { schedule } from '../store/schedule.store';
import { discountState } from '../store/discounts.store';

import { PeakHoursDay } from '@artask/tambr-models/models/business.model';
import { AddOnType } from '@artask/tambr-models/models/add-on.model';
import { Space } from '@artask/tambr-models/models/spaces.model';
import { LineItem } from '@artask/tambr-models/models/orders.model';
import { Discount } from '@artask/tambr-models/models/discount.model';



export function getPriceBreakdown() {
  const { date, spaceId, increments, spaceOptions: selectedSpaceOptions, addOns: selectedAddOns } = selection;
  if ( !date || !spaceId || !increments?.length ) return { priceBreakdown: [], total: 0 }
  const scheduleDate = schedule.dateSchedule;
  const discounts = discountState.discounts;
  const spaceTitle = scheduleDate.calendar[ spaceId ].title;
  const space = scheduleDate.spaces.find( (space: Space) => space.id === spaceId ); 
  const scheduleSettings = settings.businessSettings.scheduleSettings;
  const slotDuration = scheduleSettings.slotDuration;
  const scheduleIncrement = scheduleSettings.scheduleIncrement;
  const slots = getSlotsFromIncrements( increments, slotDuration, scheduleIncrement );
  const duration = slots.length * scheduleSettings.slotDuration;
  const paymentSettings = settings.businessSettings.paymentSettings;
  const discountStrategy = settings.businessSettings?.discountSettings?.multipleDiscountStrategy ?? 'combine';
  const businessCustomerId = customer.businessCustomer?.id ?? null;
  const customFees = paymentSettings.customFees ?? [];
  const tax = paymentSettings?.tax ?? 0;
  const orderIsTaxable = tax && paymentSettings.bookingTaxEnabled;
  const addOnTypeIsTaxable = (type: AddOnType) => 
    tax && paymentSettings.addOnTaxEnabled && paymentSettings.addOnTaxTypes.includes( type );

  const lineItems: LineItem[] = [];
  let bookingAmount = getBookingAmount( date, slots, space );
  let subtotal = bookingAmount;

  lineItems.push({ label: `${ spaceTitle } (${ formatDuration(duration) })`, amount: bookingAmount });

  const relevantDiscounts = getRelevantDiscounts( discounts, date, slots, spaceId, bookingAmount, slotDuration, businessCustomerId );

  const paymentMethodType = 'card';

  // space options
  if ( selectedSpaceOptions?.length ) {
    for ( const option of selectedSpaceOptions ) {
      let valueAmount: number;
      if ( option.amount > 0 && option.type !== 'no-price-change' ) {
        if ( option.type.includes('percentage') ) {
          valueAmount = Math.ceil( bookingAmount * ( option.amount / 100 ) ); 
          if ( option.round ) valueAmount = Math.round( valueAmount / 100 ) * 100;
        } else if ( option.type.includes('amount') ) {
          valueAmount = option.amount * slots.length;
        }

        const optionLabel = `${option.optionName}: ${option.valueName}`;
        if ( option.type.includes('subtract') ) {
          lineItems.push({ label: optionLabel, amount: valueAmount, type: "discount" });
          bookingAmount -= valueAmount;
          subtotal -= valueAmount;
        } else if ( option.type.includes('add') ) {
          lineItems.push({ label: optionLabel, amount: valueAmount });
          bookingAmount += valueAmount;
          subtotal += valueAmount;
        }
      }
    }
  }

  // discounts
  let discountTotal = 0;
  if ( relevantDiscounts?.length ) {  
    if ( discountStrategy === 'combine' ) {
      let discountedBookingAmount = bookingAmount;
      for ( const discount of relevantDiscounts ) {
        const discountAmount = getDiscountAmount( discount, discountedBookingAmount );
        lineItems.push({ label: discount.title, amount: discountAmount, type: 'discount' });
        discountTotal += discountAmount;
        discountedBookingAmount -= discountAmount;
      }
      bookingAmount = discountedBookingAmount;
      subtotal = discountedBookingAmount;
    } else {
      const discount = getExtremeDiscount( relevantDiscounts, bookingAmount, discountStrategy );
      discountTotal = getDiscountAmount( discount, bookingAmount );
      lineItems.push({ label: discount.title, amount: discountTotal, type: 'discount' });
      bookingAmount -= discountTotal;
      subtotal -= discountTotal;
    }
  }

  // add-ons
  let addOnAmount = 0;
  let addOnTaxableAmount = 0;
  let addOnNonTaxableAmount = 0;

  if ( selectedAddOns.length ) {
    const addOnLines: LineItem[] = [];
    for ( const addOn of selectedAddOns ) {
      let addOnPrice = addOn.price;
      if ( addOn.priceScope === 'slot' ) addOnPrice *= slots.length;
      const addOnPriceTotal = addOnPrice * ( addOn.quantity ?? 1 );
      const addOnQuantityPrefix = addOn.quantity > 1 ? addOn.quantity + ' × ' : '';
      const addOnLabel = addOnQuantityPrefix + addOn.title;
      
      addOnLines.push({ label: addOnLabel, amount: addOnPriceTotal, type: 'addOn', level: 2 });
      addOnAmount += addOnPriceTotal;

      if ( addOnTypeIsTaxable( addOn.type ) ) { 
        addOnTaxableAmount += addOnPriceTotal; 
      } else { 
        addOnNonTaxableAmount += addOnPriceTotal; 
      }
    }
    lineItems.push({ label: 'Add-ons', amount: addOnAmount });
    if ( addOnTaxableAmount ) {
      const addOnTaxAmount = Math.round( addOnTaxableAmount * (tax / 10000) );
      addOnLines.push({ label: 'Tax', amount: addOnTaxAmount, type: 'addOn', level: 2 });
    }
    if ( addOnLines.length ) lineItems.push( ...addOnLines );
    subtotal += addOnAmount;
  }

  // custom fees
  let customFeeTotalTaxableAmount = 0;
  let customFeeTotalNonTaxableAmount = 0;
  let customFeeTotalTaxAmount = 0;

  if ( customFees.length ) {
    console.log( 'customFees', customFees );
    for ( const customFee of customFees ) {
      if ( customFee?.paymentMethod?.length && !customFee?.paymentMethod.includes( paymentMethodType ) ) continue;
      let customFeeAmount = customFee.amount;
      if ( customFee.type === '%' ) {
        customFeeAmount = Math.ceil( bookingAmount * ( customFee.amount / 100 ) ); 
        if ( customFee.round ) customFeeAmount = Math.round( customFeeAmount / 100 ) * 100;
      }
      lineItems.push({ label: customFee.title, amount: customFeeAmount, type: 'customFee' }); 
      subtotal += customFeeAmount;
      if ( customFee.taxable ) {
        customFeeTotalTaxableAmount += customFeeAmount;
        customFeeTotalTaxAmount += Math.round( customFeeAmount * (tax / 10000) );
      } else {
        customFeeTotalNonTaxableAmount += customFeeAmount;
      }
    }
  }

  // subtotal
  lineItems.push({ label: 'Subtotal', amount: subtotal, type: 'subtotal' });
  let total = subtotal;

  // credit
  let creditAmount = 0;
  if ( customer.businessCustomer?.credit ) { 
    let availableCredit = customer.businessCustomer.credit;
    let applicableCredit: number;

    // credit (discounted) booking amount
    applicableCredit = (bookingAmount > availableCredit) ? availableCredit : bookingAmount;
    bookingAmount -= applicableCredit;
    creditAmount += applicableCredit;
    total -= applicableCredit;
    availableCredit -= applicableCredit;

    // credit taxable add-ons
    applicableCredit = (addOnTaxableAmount > availableCredit) ? availableCredit : addOnTaxableAmount;
    addOnTaxableAmount -= applicableCredit;
    creditAmount += applicableCredit;
    total -= applicableCredit;
    availableCredit -= applicableCredit;

    // credit non-taxable add-ons
    applicableCredit = (addOnNonTaxableAmount > availableCredit) ? availableCredit : addOnNonTaxableAmount;
    addOnNonTaxableAmount -= applicableCredit;
    creditAmount += applicableCredit;
    total -= applicableCredit;
    availableCredit -= applicableCredit;

    // credit taxable custom fees
    applicableCredit = (customFeeTotalTaxableAmount > availableCredit) ? availableCredit : customFeeTotalTaxableAmount;
    customFeeTotalTaxableAmount -= applicableCredit;
    creditAmount += applicableCredit;
    total -= applicableCredit;
    availableCredit -= applicableCredit;

    // credit non-taxable custom fees
    applicableCredit = (customFeeTotalNonTaxableAmount > availableCredit) ? availableCredit : customFeeTotalNonTaxableAmount;
    customFeeTotalNonTaxableAmount -= applicableCredit;
    creditAmount += applicableCredit;
    total -= applicableCredit;
    availableCredit -= applicableCredit;

    lineItems.push({ label: 'Credit', amount: creditAmount, type: 'credit' });
  }

  // order tax, addOn tax
  let taxTotal = 0;
  if ( orderIsTaxable ) taxTotal += Math.round( bookingAmount * (tax / 10000) );
  if ( addOnTaxableAmount ) taxTotal += Math.round( addOnTaxableAmount * (tax / 10000) );
  if ( customFeeTotalTaxableAmount ) taxTotal += Math.round( customFeeTotalTaxableAmount * (tax / 10000) );
  lineItems.push({ label: 'Tax', amount: taxTotal, type: 'tax' });  
  total += taxTotal;
  
  if ( total < 50 ) total = 0;
  lineItems.push({ label: 'Total', amount: total, type: 'total' });

  return { priceBreakdown: lineItems, total: total }
}


function getExtremeDiscount( discounts: Discount[], subtotal: number, strategy: 'lowest' | 'highest' ) {
  if ( !discounts.length ) return null;
  let extremeDiscountAmount = getDiscountAmount( discounts[0], subtotal );
  let extremeDiscount = discounts[0];
  for ( let discount of discounts ) {
    const discountAmount = getDiscountAmount( discount, subtotal );
    if ( strategy === 'lowest' && discountAmount < extremeDiscountAmount ) {
      extremeDiscountAmount = discountAmount;
      extremeDiscount = discount;
    }
    if ( strategy === 'highest' && discountAmount > extremeDiscountAmount ) {
      extremeDiscountAmount = discountAmount;
      extremeDiscount = discount;
    }
  }
  return extremeDiscount;
}


function getDiscountAmount( discount: Discount, amount: number ) {
  let discountAmount: number;
  switch( discount.type ) {
    case '%': 
      discountAmount = Math.ceil( amount * ( discount.value / 100 ) ); 
      if ( discount.round ) discountAmount = Math.round( discountAmount / 100 ) * 100;
      break;
    case '$': 
      discountAmount = discount.value; 
      break;
  }
  return discountAmount;
}


function getRelevantDiscounts( 
  discounts: Discount[],
  date: string,
  slots: number[],
  spaceId: string,
  subtotal: number,
  slotDuration: number,
  businessCustomerId: string,
) {
  return discounts.filter( (discount: Discount) => {
         
    // date logic
    const startDate = discount.datesActive.startDate.toDate();
    const isEndDateEnabled = !!discount.datesActive.endDateEnabled;
    const endDate = isEndDateEnabled ? discount.datesActive.endDate.toDate() : null;
    const todayDate = spacetime.now( settings.businessSettings.timezone ).toNativeDate();

    if ( todayDate < startDate ) return false;       
    if ( isEndDateEnabled && todayDate > endDate ) return false; 

    // customer logic
    if ( discount.customers.scope !== 'any' ) {
      let found = false;
      for ( const discountCustomer of discount.customers.values ) {
        if ( businessCustomerId !== null && discountCustomer.id === businessCustomerId ) { found = true; break; }
      }
      if (!found) return false;
    } 
    
    // space logic
    if ( discount.spaces.scope !== 'any' && !discount.spaces.values.includes( spaceId ) ) return false;
    
    // day logic
    if ( discount.days.scope !== 'any' ) {
      const day = spacetime( date ).day();
      if ( !discount.days.values.includes(day) ) return false;
    } 

    // dates logic
    if ( discount.dates.scope !== 'any' ) {
      const orderDate = spacetime( date );
      const discountStartDate = spacetime( discount.dates.startDate.toDate() );
      const discountEndDate = spacetime( discount.dates.endDate.toDate() );
      if ( orderDate.isBefore(discountStartDate) || orderDate.isAfter(discountEndDate) ) return false;
    } 

    // time logic
    if ( discount.time.scope !== 'any' ) {
      const orderStarts = slots[0];
      const orderEnds = slots[ slots.length-1 ] + slotDuration * 60 * 1000;
      const startLimit = spacetime( date, settings.businessSettings.timezone ).time( discount.time.start );
      let endLimit = spacetime( date, settings.businessSettings.timezone ).time( discount.time.end );
      if ( endLimit.isBefore( startLimit ) ) endLimit = endLimit.add( 1, 'day' );
      if ( spacetime( orderStarts ).isBefore( startLimit ) || spacetime( orderEnds ).isAfter( endLimit ) ) return false;
    } 

    // time of booking logic 
    if ( discount?.bookingTime?.enabled ) {
      const timeframe = discount.bookingTime.timeframe;
      const currentTime = spacetime.now( settings.businessSettings.timezone );
      const orderStartTime = spacetime( slots[0], settings.businessSettings.timezone );
      const timeframeStartTime = orderStartTime.subtract( discount.bookingTime.duration, discount.bookingTime.unit );
      if ( timeframe === 'inside' && currentTime.isBefore( timeframeStartTime ) ) return false;
      if ( timeframe === 'outside' && !currentTime.isBefore( timeframeStartTime ) ) return false;
    }

    // min requirement logic
    if ( discount.minRequirement.type !== 'none' ) {
      if ( discount.minRequirement.type === 'slots' && discount.minRequirement.slots > slots.length ) return false;
      if ( discount.minRequirement.type === 'amount' && subtotal < +discount.minRequirement.amount ) return false;
    }

    // max requirement logic
    if ( discount.maxRequirement.type !== 'none' ) {
      if ( discount.maxRequirement.type === 'slots' && discount.maxRequirement.slots < slots.length ) return false;
      if ( discount.maxRequirement.type === 'amount' && subtotal > +discount.maxRequirement.amount ) return false;
    }

    return true;
  })
}



function getSlotsFromIncrements( increments: number[], slotDuration: number, scheduleIncrement: number ) {
  const incrementsPerSlot = slotDuration / scheduleIncrement;
  const slots: number[] = [];
  for (let i = 0; i < increments.length; i += incrementsPerSlot) { slots.push( increments[i] ) }
  return slots;
}


function getBookingAmount( date: string, slots: number[], space: Space ) {
  const timezone = settings.businessSettings.timezone;
  const slotDuration = settings.businessSettings.scheduleSettings.slotDuration;
  const businessPeakHours = settings.businessSettings.scheduleSettings.peakHours;

  const orderDate = spacetime( date, timezone );  
  if ( !space.peakPriceEnabled || !space.peakPrice ) {
    return slots.length * space.basePrice;
  }

  const dayPeakHours: PeakHoursDay = businessPeakHours[ orderDate.dayName() ];
  const { peakHoursStart: dayPeakHoursStart, peakHoursEnd: dayPeakHoursEnd } = getPeakHourRange( orderDate, dayPeakHours );

  const prevDate = orderDate.subtract( 1, 'day' );
  const prevDayPeakHours: PeakHoursDay = businessPeakHours[ prevDate.dayName() ];
  const { peakHoursStart: prevDayPeakHoursStart, peakHoursEnd: prevDayPeakHoursEnd } = getPeakHourRange( prevDate, prevDayPeakHours );

  let bookingAmount = 0;
  for ( const slot of slots ) {
    const slotStarts = spacetime( slot, timezone );
    const slotEnds = spacetime( slot, timezone ).add( slotDuration, 'minute' );
    const dayPeakPriceApplies = dayPeakHours.enabled && slotStarts.isBefore( dayPeakHoursEnd ) && slotEnds.isAfter( dayPeakHoursStart );
    const prevDayPeakPriceApplies = prevDayPeakHours.enabled && slotStarts.isBefore( prevDayPeakHoursEnd ) && slotEnds.isAfter( prevDayPeakHoursStart );
    const slotPrice = (dayPeakPriceApplies || prevDayPeakPriceApplies) ? space.peakPrice : space.basePrice;
    bookingAmount += slotPrice;
  }

  return bookingAmount;
}


function getPeakHourRange( date: Spacetime, dayPeakHours: PeakHoursDay ) {
  if ( !dayPeakHours?.enabled ) return { peakHoursStart: null, peakHoursEnd: null }
  const peakHoursStart = date.time( dayPeakHours.starts );
  let peakHoursEnd = date.time( dayPeakHours.ends );
  if ( !peakHoursEnd.isAfter( peakHoursStart ) ) peakHoursEnd = peakHoursEnd.add( 1, 'day' );
  return { peakHoursStart, peakHoursEnd }
}